import { Alert, App as AntApp, Button, Card, Form, Input, Popconfirm, Space, Table, Tabs, Tag, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { api, errorText } from "../api";
import { dateTime } from "../format";
import { canManage, useSession } from "../session";
import { useResource } from "../useResource";

export type PolicyTopic = "RETURNS" | "SHIPPING" | "WARRANTY" | "PAYMENT";

/** `PolicyRepository.VersionRow` */
export interface PolicyVersion {
  topic: PolicyTopic;
  version: number;
  title: string;
  body: string;
  createdBy: number;
  createdAt: string;
}

export interface TopicOverview {
  topic: PolicyTopic;
  label: string;
  publishedVersion: number;
  versions: PolicyVersion[];
}

interface Draft {
  title: string;
  body: string;
}

/**
 * Mağaza politikaları (iade, kargo, garanti, ödeme). Asistan bu konulardaki soruları yalnızca yayındaki metinle
 * cevaplar; yayında metin yoksa uydurmaz, temsilciye yönlendirir. Sürümler değişmez; geri alma, eski bir sürümü yeniden
 * yayınlamaktır.
 */
export function PoliciesPage() {
  const { data, error, reload } = useResource(useCallback(
    () => api.get<{ topics: TopicOverview[] }>("/api/console/policies"), []));

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!data) {
    return null;
  }
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Title level={3}>Politikalar</Typography.Title>
      <Typography.Paragraph type="secondary">
        Asistan iade, kargo, garanti ve ödeme sorularını yalnızca burada yayında olan metne dayanarak cevaplar.
        Yayında metin olmayan konuda bilgi uydurmaz; müşteriyi temsilciye yönlendirir.
      </Typography.Paragraph>
      <Tabs items={data.topics.map((t) => ({
        key: t.topic,
        label: <Space size={4}>{t.label}{t.publishedVersion === 0 && <Tag>Yayında yok</Tag>}</Space>,
        children: <TopicPanel overview={t} onChange={reload} />,
      }))} />
    </Space>
  );
}

function TopicPanel({ overview, onChange }: { overview: TopicOverview; onChange: () => void }) {
  const me = useSession((s) => s.me);
  const manage = canManage(me);
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<Draft>();
  const [saving, setSaving] = useState(false);
  const published = overview.versions.find((v) => v.version === overview.publishedVersion);

  // Yeni sürüm formu yayındaki (yoksa en son) sürümden başlar.
  useEffect(() => {
    const base = published ?? overview.versions[0];
    form.setFieldsValue(base ? { title: base.title, body: base.body } : { title: overview.label, body: "" });
  }, [form, overview, published]);

  async function create(draft: Draft) {
    setSaving(true);
    try {
      const { version } = await api.post<{ version: number }>(`/api/console/policies/${overview.topic}/versions`, draft);
      message.success(`Sürüm ${version} oluşturuldu; yayınlamak için listeden seçin`);
      onChange();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  async function publish(version: number) {
    try {
      await api.post(`/api/console/policies/${overview.topic}/versions/${version}/publish`);
      message.success(`Sürüm ${version} yayında`);
      onChange();
    } catch (e) {
      message.error(errorText(e));
    }
  }

  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      {published ? (
        <Card title={`Yayında: ${published.title}`} extra={<Tag color="green">Sürüm {published.version}</Tag>}>
          <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{published.body}</Typography.Paragraph>
        </Card>
      ) : (
        <Alert type="info" showIcon
          title={`${overview.label} için yayında metin yok; asistan bu konudaki soruları temsilciye yönlendirir.`} />
      )}
      <Card title="Sürümler">
        <Table<PolicyVersion> rowKey="version" dataSource={overview.versions} pagination={false} size="small"
          locale={{ emptyText: "Henüz sürüm yok" }}
          columns={[
            { title: "Sürüm", dataIndex: "version", width: 90,
              render: (v: number) => (v === overview.publishedVersion ? <Space>{v}<Tag color="green">Yayında</Tag></Space> : v) },
            { title: "Başlık", dataIndex: "title" },
            { title: "Oluşturulma", dataIndex: "createdAt", render: dateTime },
            ...(manage ? [{
              title: "", key: "actions", width: 140,
              render: (_: unknown, row: PolicyVersion) => row.version === overview.publishedVersion ? null : (
                <Popconfirm title={`Sürüm ${row.version} yayınlansın mı?`}
                  description={row.version < overview.publishedVersion ? "Önceki bir sürüme geri dönülür." : undefined}
                  okText="Yayınla" cancelText="Vazgeç" onConfirm={() => publish(row.version)}>
                  <Button size="small">{row.version < overview.publishedVersion ? "Geri al" : "Yayınla"}</Button>
                </Popconfirm>
              ),
            }] : []),
          ]} />
      </Card>
      {manage && (
        <Card title="Yeni sürüm" extra={<Typography.Text type="secondary">Yayındaki sürümden başlar</Typography.Text>}>
          <Form form={form} layout="vertical" onFinish={create}>
            <Form.Item label="Başlık" name="title" rules={[{ required: true, whitespace: true, max: 120 }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Metin" name="body" rules={[{ required: true, whitespace: true, max: 4000 }]}
              tooltip="Süre, tutar ve koşulları açıkça yazın; asistan yalnızca bu metindeki bilgileri aktarır">
              <Input.TextArea autoSize={{ minRows: 4 }} showCount maxLength={4000} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Sürüm oluştur</Button>
          </Form>
        </Card>
      )}
    </Space>
  );
}
